<template>
    <div class="d-flex flex-column flex-fill chronic-absence-container">
        <div class="header-section">
            <a @click="goBack" data-test="chronic-absence-back-btn" class="back-button" tabindex="0" @keydown.enter="goBack" @keydown.space="goBack">
                <i class="fal fa-chevron-left ml-3"></i>Back to Launchpad
            </a>
            <div class="d-flex pt-3 px-5 pb-1 ml-3" style="padding-bottom: 24px !important; padding-left: 0 !important; align-items: center;">
                <span style="font-size: 24px; padding-right: 10px;">Chronic Absence</span>
                <fe-info-tip
                    color="#495B8F !important"
                    icon="fal fa-info-circle"
                    size="18px"
                    tooltip="Chronic absence is when a student is absent (excused or unexcused) for at least 10% of the school days. The overall rate indicates the percentage of students who are chronically absent in a given time period."
                >
                </fe-info-tip>
            </div>

            <div v-if="ready">
                <div v-if="prefs.sisReportedConfigured" >
                    <div class="d-flex" style="padding-left: 4px !important;">
                        <fe-filter-btn
                            v-model="filters.school_id"
                            ref="ca-school-filter"
                            rootProperty="schools"
                            title="School"
                            :items="combinedSchoolsData.schools"
                            :closeOnSelect="false"
                            multiple
                            showFirstSelection
                            showClearButton
                            showApplyButton
                            @clear="clearFilter('school')"
                            @apply="apply('ca-school-filter')"
                            @hook:mounted="syncSchoolFilter"
                            data-test="ca-school-filter-btn"
                        />
                        <fe-filter-btn
                            v-model="filters.grade_id"
                            ref="ca-grade-filter"
                            rootProperty="grades"
                            title="Grade Level"
                            :url="gradeUrl"
                            :closeOnSelect="false"
                            multiple
                            showFirstSelection
                            showClearButton
                            showApplyButton
                            @clear="clearFilter('grade')"
                            @apply="apply('ca-grade-filter')"
                            data-test="ca-grade-level-filter-btn"
                        />
                        <fe-filter-btn
                            v-model="filters.ethnicity"
                            ref="ca-ethnicity-filter"
                            rootProperty="ethnicities"
                            itemValue="ethnicity"
                            itemText="display_name"
                            title="Race/Ethnicity"
                            :url="ethnicityUrl"
                            :closeOnSelect="false"
                            multiple
                            showFirstSelection
                            showClearButton
                            showApplyButton
                            @clear="clearFilter('ethnicity')"
                            @apply="apply('ca-ethnicity-filter')"
                            data-test="ca-ethnicity-filter-btn"
                        />
                        <fe-filter-btn
                            v-model="filters.gender"
                            ref="ca-gender-filter"
                            rootProperty="genders"
                            itemValue="gender"
                            itemText="display_name"
                            title="Gender"
                            :url="genderUrl"
                            :closeOnSelect="false"
                            multiple
                            showFirstSelection
                            showClearButton
                            showApplyButton
                            @clear="clearFilter('gender')"
                            @apply="apply('ca-gender-filter')"
                            data-test="ca-gender-filter-btn"
                        />
                        <more-filters 
                            ref="ca-more-filters" 
                            :value="moreFiltersRawData" 
                            :key="Object.keys(moreFiltersRawData || {}).sort().join(',')"
                            source-name="chronic-absence"
                            @apply="applyMore">
                        </more-filters>
                        <v-spacer/>
                        <toggle-button
                            v-if="phaseTwoFlag"
                            :items="[
                                { name: 'Charts', icon: 'far fa-chart-bar' },
                                { name: 'Data Wall', icon: 'far fa-table' }
                            ]"
                            v-model="viewMode"
                            class="toggle-btn"
                        />
                    </div>

                    <filter-chips
                        :moreFilters="demoFilters"
                        :quickFilters="filters"
                        :updateChips="updateChips"
                        @clearAllFilters="clearAllFilters"
                        @removeChipFilter="removeChipFilter"
                        @reloadupdateChips="updateChips = false"
                        @filterChips="filterChips = $event"
                        dataTestLocation="ca-page-"
                        >
                    </filter-chips>

                    <div v-if="viewMode.name === 'Charts'">
                        <fe-tabs
                            class="ca-tabs"
                            id="ca-page-tabs"
                            :tabs="tabs"
                            :value="activeTabIndex"
                            @tabClicked="tabClicked"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div v-if="ready">
            <div v-if="prefs.sisReportedConfigured" >
                <div
                    v-if="activeTab.show === 'ethnicity' && viewMode.name === 'Charts'"
                    style="padding: 24px 20px 0px 52px;"
                    class="remote-dem-combo"
                >
                    <div style="float: left" class="center-demo-combo-title">Explore by:</div>
                    <fe-remote-combo
                        v-model="selectedDemographic"
                        :items="demographicOptions"
                        :clearable="false"
                        hide-details
                        @change="chartParamChange('demographic', $event)"
                        class="demographic-select mb-0"
                        data-test="chronic-absence-explore-demographic-filter"
                    />
                </div>

                <div v-if="viewMode.name === 'Charts'">
                    <template v-for="(tab, index) in tabs">
                        <v-container
                            v-if="activeTab.show === tab.show"
                            fluid
                            :class="['chart-area', { 'demographic-tab': activeTab.show === 'ethnicity' }]"
                            :key="`tab-${index}`"
                        >
                            <chronic-absence-rate-chart
                                ref="ca-rate-chart"
                                :data="tab"
                                :year="selectedTimePeriod"
                                :ready="tabData[tab.show].loaded"
                                :error="tabData[tab.show].error"
                                :noFilterResults="noFilterResults"
                                :noSchoolGroups="noSchoolGroups"
                                :canManageSchools="canManageSchools"
                                :toggle="countsToggle"
                                :drilldownEnabled="true"
                                :phaseTwoFlag="phaseTwoFlag"
                                @change="chartParamChange"
                                @clearAllFilters="clearAllFilters"
                                @barClick="handleChartDrilldown"
                            />
                        </v-container>
                    </template>
                </div>

                <template v-for="(tab, index) in tabs">
                    <v-container
                        v-if="activeTab.show === tab.show && viewMode.name === 'Data Wall'"
                        fluid
                        class="data-area pa-0"
                        :key="`tab-${index}`"
                        :style="{ height: datawallHeight }"
                    >
                        <div v-if="isFilteringByMealStatus && !$hasSecurity('DRILLDOWN_MEAL_STATUS')" class="permission-empty-state">
                            <div class="permission-empty-content">
                                <div class="permission-icon">
                                    <i class="far fa-lock"></i>
                                </div>
                                <div class="permission-title">Permission needed to view students</div>
                                <div class="permission-message">
                                    You need the "Search Students by Meal Status" permission to view these students. Contact your administrator to request access.
                                </div>
                                <div class="permission-actions">
                                    <fe-btn 
                                        usage="primary"
                                        href="https://support.renaissance.com/s/article/Manage-User-Permissions-1752691188951?language=en_US"
                                        target="_blank"
                                    >
                                        Learn More <i class="far fa-external-link ml-2"></i>
                                    </fe-btn>
                                </div>
                            </div>
                        </div>
                        <div v-else class="chronic-absence-grid" style="width: 100%; height: 100%;">
                            <chronic-absence-details
                                :students="gridData"
                                :columnDefs="columnDefs"
                                :loading="false"
                                :show="true"
                                :isModal="false"
                                :containerHeight="datawallHeight"
                                :csvFilename="datawallCsvFilename"
                                @goToStudentProfile="goToStudentProfile"
                                @tagsUpdated="loadDataWallData"
                                :scoreFilters="scoreFilters"
                            />
                        </div>
                    </v-container>
                </template>
            </div>

            <div v-else>
                <restricted/>
            </div>
        </div>

        <fe-crud ref="prefsCrud" :config="prefsCrudConfig" @read="prefsFetched" autoload/>
        <fe-crud ref="crud" :config="crudConfig"/>

        <chronic-absence-details
            :students="gridData"
            :columnDefs="columnDefs"
            :loading="chronicAbsenceDetailsLoading"
            :show="showChronicAbsenceDetails"
            :title="chronicAbsenceDetailsTitle"
            :isModal="true"
            :csvFilename="datawallModalCSVFilename"
            :drilldownParams="chronicAbsenceDetailsParams"
            @goToStudentProfile="goToStudentProfile"
            @close="showChronicAbsenceDetails = false"
            @tagsUpdated="reloadModalData"
            :scoreFilters="scoreFilters"
        />

        <fe-dialog
            v-if="showMealStatusPermissionModal"
            title="Permission Needed to View Students"
            width="500"
            acceptButtonText="Okay"
            dismissButtonText=""
            :footer="false"
            class="permission-dialog"
            @accept="showMealStatusPermissionModal = false"
            @close="showMealStatusPermissionModal = false"
        >
            <div class="permission-modal-content">
                <p class="permission-message">
                    You need the "Search Students by Meal Status" permission to view these students. Contact your administrator to request access.
                </p>
                <div class="permission-actions">
                    <a 
                        href="https://support.renaissance.com/s/article/Manage-User-Permissions-1752691188951?language=en_US"
                        target="_blank"
                        class="learn-more-link"
                    >
                        Learn More  <i class="far fa-external-link"></i>
                    </a>
                    <fe-btn 
                        usage="primary" 
                        @click="showMealStatusPermissionModal = false"
                    >
                        Okay
                    </fe-btn>
                </div>
            </div>
        </fe-dialog>
    </div>
</template>

<script>
import { mapState } from "vuex"
import MoreFilters from "./MoreFilters"
import FilterChips from "../../common/FilterChips.vue"
import ChronicAbsenceRateChart from "../../charts/ChronicAbsenceRateChart.vue"
import ToggleButton from "@/components/common/button/ToggleButton"
import baseConfig from "./baseConfig"
import { singleSeriesPattern, twoSeriesPatterns, fiveSeriesPatterns } from "../../charts/chartFormats/patterns/seriesPatterns"
import NoFilterResults from '../../charts/emptyStateMessaging/NoFilterResults.vue'
import Restricted from "./messaging/Restricted.vue"
import ChronicAbsenceDetails from './ChronicAbsenceDetails.vue'
import * as DemographicUtil from "../../common/util/DemographicUtil"
import InterventionColumn from '@/components/modules/datawall/renderers/InterventionColumn'
import ScoreHeader from '@/components/modules/datawall/headers/ScoreHeader'

export default {
    name: 'ChronicAbsenceReporting',

    components: {
        MoreFilters,
        FilterChips,
        ChronicAbsenceRateChart,
        NoFilterResults,
        Restricted,
        ToggleButton,
        ChronicAbsenceDetails,
        interventionColumn: InterventionColumn,
        scoreHeader: ScoreHeader,
    },

    data() {
        let me = this
        return {
            prefs: {},
            activeTab: {
                name: 'Overall',
                show: 'district',
                path: '#'
            },
            selectedTimePeriod: {
                name: 'Last 2 Years (Monthly)',
                id: '2M'
            },
            viewMode: {
                name: 'Charts',
                icon: 'fa-chart-bar'
            },
            tabData: {
                district: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                school: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                grade: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                ethnicity: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                school_group: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
            },
            selectedDemographic: 'ethnicity',
            filters: {
                school_id: undefined,
                grade_id: undefined,
                ethnicity: undefined,
                gender: undefined
            },
            demoFilters: {},
            moreFiltersRawData: {},
            filterStateChange: false,
            filterChips: [],
            updateChips: false,
            countsToggle: true,
            useDefaultToggle: true,
            districtHasSchoolGroups: null,
            userHasSchoolGroups: null,
            combinedSchoolsData: { schools: [] },
            ready: false,
            rateDescriptor: '',
            columnDefs: [
                {
                    headerCheckboxSelection: true,
                    checkboxSelection: true,
                    maxWidth: 70,
                    colId: 'checkbox_column',
                    headerClass: 'ag-bold-header',
                    suppressMenu: true,
                    sortable: false,
                    resizable: false
                },
                {
                    headerName: "Student",
                    field: "student_full_name",
                    headerClass: 'ag-bold-header',
                    sortable: true,
                    width: 195,
                    cellRenderer: params => {
                        return `<span style="cursor: pointer; color: #006C90;" class="student-name-cell">${params.value}</span>`
                    }
                },
                {
                    headerName: "School",
                    field: "school_name",
                    headerClass: 'ag-bold-header',
                    sortable: true
                },
                {
                    headerName: "Current Grade Level",
                    field: "grade_level",
                    headerClass: 'ag-bold-header',
                    sortable: true,
                    minWidth: 70,
                    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                        // Use grade_rank field for proper grade sorting
                        const gradeRankA = nodeA.data.grade_rank
                        const gradeRankB = nodeB.data.grade_rank
                        
                        if (gradeRankA && gradeRankB && gradeRankA !== gradeRankB) {
                            return gradeRankA - gradeRankB
                        }
                        
                        // Fallback to alphabetical if no grade rank available
                        return valueA.localeCompare(valueB)
                    }
                },
                {
                    headerName: "State ID",
                    field: "student_state_id",
                    headerClass: 'ag-bold-header',
                    sortable: true,
                    hide: true
                },
                {
                    headerName: "District ID",
                    field: "student_district_id",
                    headerClass: 'ag-bold-header',
                    sortable: true
                },
                {
                    headerName: "Interventions",
                    field: "interventions",
                    headerComponentFramework: 'scoreHeader',
                    headerComponentParams: {
                        field: 'interventions',
                        displayName: '<i style="font-size: 18px;" aria-label="View intervention group column header" class="far fa-hands-heart"></i>',
                        showScoreFilters: true,
                        filterType: 'interventionLevel',
                        filterValues: me.scoreFilters?.length && me.scoreFilters.find(x => x.field === 'interventions')
                            ? me.scoreFilters.find(x => x.field === 'interventions').filters : {},
                        filterValueName: 'Score',
                        isFilterActive: me.scoreFilters?.length && me.scoreFilters.find(x => x.field === 'interventions') ? true : false,
                        onApplyFilters: me.onApplyFilters,
                    },
                    minWidth: 80,
                    maxWidth: 80,
                    filter: 'agSetColumnFilter'
                },
                {
                    headerName: "SIS Reported Rate",
                    field: "sis_attendance_rate",
                    cellStyle: { textAlign: 'right' },
                    headerClass: 'ag-right-aligned-header',
                    sortable: true,
                    minWidth: 60,
                    maxWidth: 150,
                    valueFormatter: params => {
                        return Number(params.value).toFixed(2)
                    },
                    comparator: (valueA, valueB) => {
                        const numA = parseFloat(valueA) || 0
                        const numB = parseFloat(valueB) || 0
                        return numA - numB
                    }
                },
                {
                    headerName: "Days Missed",
                    field: "sis_days_missed",
                    cellStyle: { textAlign: 'right' },
                    headerClass: 'ag-right-aligned-header',
                    sortable: true,
                    comparator: (valueA, valueB) => {
                        const numA = parseFloat(valueA) || 0
                        const numB = parseFloat(valueB) || 0
                        return numA - numB
                    },
                    minWidth: 60,
                    maxWidth: 150
                },
                {
                    headerName: "Possible Attendance Days",
                    field: "sis_possible_days",
                    cellStyle: { textAlign: 'right' },
                    headerClass: 'ag-right-aligned-header',
                    sortable: true,
                    comparator: (valueA, valueB) => {
                        const numA = parseFloat(valueA) || 0
                        const numB = parseFloat(valueB) || 0
                        return numA - numB
                    },
                    minWidth: 60,
                    maxWidth: 150
                },
                {
                    headerName: "Ethnicity",
                    field: "ethnicity",
                    headerClass: 'ag-bold-header',
                    sortable: true,
                    hide: true
                },
                {
                    headerName: "Gender",
                    field: "gender",
                    headerClass: 'ag-bold-header',
                    sortable: true,
                    hide: true,
                    minWidth: 50,
                    maxWidth: 120,
                },
                {
                    headerName: "Tags",
                    field: "student_tags",
                    headerClass: 'ag-bold-header',
                    sortable: true,
                    hide: true,
                    width: 244,
                    cellRenderer: params => {
                        if (!params.data.student_tags || !Array.isArray(params.data.student_tags) || params.data.student_tags.length === 0) {
                            return ''
                        }
                        return params.data.student_tags.map(tag => {
                            return `<span style="color: ${tag.tag_color};">${tag.tag_name}</span>`
                        }).join(' <span style="color: #E0E1E6;">|</span> ')
                    },
                    valueGetter: params => {
                        // For CSV export, return pipe-separated tag names
                        if (!params.data.student_tags || params.data.student_tags.length === 0) {
                            return ''
                        }
                        return params.data.student_tags.map(tag => tag.tag_name).join(' | ')
                    },
                    comparator: (valueA, valueB) => {
                        // Sort by concatenated tag names
                        const tagsA = valueA ? valueA.map(t => t.tag_name).join(' ') : ''
                        const tagsB = valueB ? valueB.map(t => t.tag_name).join(' ') : ''
                        return tagsA.localeCompare(tagsB)
                    }
                }
            ],
            gridData: [],
            gridOptions: {
                immutableData: true,
                rowHeight: 40,
                suppressHorizontalScroll: true,
                suppressAutoSize: true
            },
            showChartDetailsDialog: false,
            chartDetailsTitle: 'Student Data',
            chartDetailsInfo: '',
            showChronicAbsenceDetails: false,
            chronicAbsenceDetailsLoading: true,
            lastActiveTab: null,
            chronicAbsenceDetailsParams: {},
            datawallModalCSVFilename: 'Chronically Absent Students',
            chronicAbsenceDetailsTitle: 'Chronically Absent Students',
            showMealStatusPermissionModal: false,
            savedFilters: [],
            scoreFilters: [],
            schoolsLoading: false,
        }
    },

    computed: {
        ...mapState('global', ['currentYear', 'districtPreferences', 'sessionUser', 'defaultSchool']),
        datawallHeight() {
            return 'calc(100vh - 270px)'
        },
        demographics() {
            return this.$store.getters['global/demographicsSearchable']
        },

        demographicOptions() {
            const baseOptions = [{
                name: 'Race/Ethnicity',
                id: 'ethnicity'
            }, {
                name: 'Gender',
                id: 'gender'
            }, {
                name: 'Meal Status',
                id: 'meal_status'
            }, {
                name: 'Disability',
                id: 'disability'
            }, {
                name: 'ELP Level',
                id: 'ell_level'
            }]

            return baseOptions.filter(option => {
                if (option.id === 'meal_status') {
                    return this.$hasSecurity('SEARCH_MEAL_STATUS')
                }
                return true
            })
        },

        datawallCsvFilename() {
            // For datawall, use the current school year's date range
            if (this.currentYear?.start_datetime && this.currentYear?.end_datetime) {
                const startDate = this.$dayjs(this.currentYear.start_datetime).format('M/D/YYYY')
                const endDate = this.$dayjs(this.currentYear.end_datetime).format('M/D/YYYY')
                return `Chronically Absent Students ${startDate}_${endDate}`
            }
            return 'Chronically Absent Students'
        },

        modalCsvFilename() {
            // For modal (drilldown from chart), use the specific bar's date range
            const currentData = this.tabData[this.activeTab.show]?.data?.[0]
            if (currentData && currentData.start_display_date && currentData.end_display_date) {
                const startDate = this.$dayjs(currentData.start_display_date).isValid() ? this.$dayjs(currentData.start_display_date).format('M/D/YYYY') : currentData.start_display_date
                const endDate = this.$dayjs(currentData.end_display_date).isValid() ? this.$dayjs(currentData.end_display_date).format('M/D/YYYY') : currentData.end_display_date
                return `Chronically Absent Students ${startDate}_${endDate}`
            }
            return 'Chronically Absent Students'
        },

        tabs() {
            const baseTabs = [{
                name: 'Overall',
                show: 'district',
                config: this.caChartConfig,
                path: '#',
            },
            {
                name: 'By School',
                show: 'school',
                config: this.caChartConfig,
                path: '#',
            },
            {
                name: 'By Grade Level',
                show: 'grade',
                config: this.caChartConfig,
                path: '#',
            },
            {
                name: 'By Demographic',
                show: 'ethnicity',
                config: this.caChartConfig,
                path: '#',
            }]
            
            // Show "By School Group" tab if user has accessible groups OR district hasn't configured groups (for messaging)
            if (this.userHasSchoolGroups || !this.districtHasSchoolGroups) {
                baseTabs.push({
                    name: 'By School Group',
                    show: 'school_group',
                    config: this.caChartConfig,
                    path: '#'
                })
            }
            
            return baseTabs
        },

        phaseTwoFlag() {
            return this.$store.getters['flags/flags']['ec-attendance-mlp2']
        },

        prefsCrudConfig() {
            let cfg = _.cloneDeep(this.$models.preferences)
            cfg.read.params.property = 'district'
            return cfg
        },

        schoolUrl() {
            return this.$models.getUrl('school', 'read')+'&school_year_id='+this.currentYear.id
        },

        schoolGroupUrl() {
            return this.$models.getUrl('schoolGroups', 'read')
        },

        gradeUrl() {
            return this.$models.getUrl('grade', 'read')+'&school_year_id='+this.currentYear.id
        },

        ethnicityUrl() {
            return this.$models.getUrl('ethnicityType', 'read')
        },

        genderUrl() {
            return this.$models.getUrl('genderType', 'read')
        },

        caChartConfig() {
            let caConfig = this.$_.cloneDeep(this.baseConfig())
            this.rateDescriptor = null

            let currentTab = this.activeTab.show

            if (this.tabData[currentTab].data && this.tabData[currentTab].loaded) {
                let grouped = {}
                let data = this.tabData[currentTab].data

                data.forEach(dataEl => {
                    if (!grouped[`${dataEl.start_date}-${dataEl.end_date}`]) grouped[`${dataEl.start_date}-${dataEl.end_date}`] = []
                    grouped[`${dataEl.start_date}-${dataEl.end_date}`].push(dataEl)
                })

                if (currentTab === 'district') {
                    this.rateDescriptor = data[0]?.hasOwnProperty('month_name') ? 'Month' : 'Year'

                    if (this.rateDescriptor === 'Year') {
                        caConfig.series = {
                            data : Object.keys(grouped).map(key => {
                                return {
                                    y: grouped[key][0].chronic_absence_rate,
                                    name: key
                                }
                            })
                        }
                        caConfig.legend.enabled = false
                        caConfig.xAxis.categories = Object.keys(grouped)
                    }  else {
                        caConfig.series = Object.keys(grouped).map(key => {
                            return {
                                name: key,
                                data: grouped[key].map(d => d.chronic_absence_rate)
                            }
                        })
                        caConfig.xAxis.categories = [...new Set(data.map(d => d.month_name))]
                        this.setAxisLabels(grouped, caConfig, this.tabData.district.info)
                    }
                } else {
                    caConfig.xAxis.categories = Object.keys(grouped)
                    caConfig.series = Object.keys(grouped).map(key => {
                        return {
                            name: key,
                            data: grouped[key].map(d => { return {y: d.chronic_absence_rate, name: currentTab === 'ethnicity' ? d[this.selectedDemographic] : d[currentTab]} })
                        }
                    })
                    caConfig.xAxis.categories = [...new Set(data.map(d => currentTab === 'ethnicity' ? d[this.selectedDemographic] : d[currentTab]))]
                    this.setAxisLabels(grouped, caConfig, this.tabData[currentTab].info)
                }

                const seriesData = this.rateDescriptor === 'Year' && currentTab === 'district' ? caConfig.series.data : caConfig.series
                this.setSeriesPattern(seriesData, caConfig)
                let paramGroupName = currentTab === 'ethnicity' ?
                    this.demographicOptions.find(d => d.id === this.selectedDemographic).name :
                    currentTab === 'school_group' ? 'School Group' : currentTab.charAt(0).toUpperCase() + currentTab.slice(1)
                this.setExportingParams(caConfig, paramGroupName)
                this.tabData[currentTab].loaded = true
            }
            
            // Ensure all data labels show, even when values are the same
            caConfig.plotOptions.column.dataLabels.allowOverlap = true
            caConfig.plotOptions.column.dataLabels.crop = false
            
            // First, set default positioning for all labels
            caConfig.plotOptions.column.dataLabels.rotation = 0
            caConfig.plotOptions.column.dataLabels.align = 'center'
            caConfig.plotOptions.column.dataLabels.verticalAlign = 'bottom'
            caConfig.plotOptions.column.dataLabels.y = 0
            
            // Override formatter to tilt labels based on actual bar width
            caConfig.plotOptions.column.dataLabels.formatter = function() {
                const minWidthForHorizontal = 30 // minimum width in pixels for horizontal labels
                
                // Check if the bar graphic exists and get its width
                if (this.point.graphic) {
                    const barWidth = this.point.graphic.getBBox().width
                    
                    if (barWidth < minWidthForHorizontal) {
                        // Bar is too narrow - mark for tilting
                        this.point.needsTilting = true
                    } else {
                        this.point.needsTilting = false
                    }
                }
                
                return this.y + '%'
            }
            
            // Add event to handle label tilting after chart is rendered
            caConfig.chart.events = caConfig.chart.events || {}
            caConfig.chart.events.render = function() {
                this.series.forEach(series => {
                    series.points.forEach(point => {
                        if (point.needsTilting && point.dataLabel) {
                            point.dataLabel.attr({
                                rotation: -45,
                                align: 'left',
                                verticalAlign: 'bottom',
                                x: point.dataLabel.x - 3,
                                y: point.dataLabel.y + 8
                            })
                        }
                    })
                })
            }
            
            this.setDataLabelToggle()
            return caConfig
        },

        filterEmpty() {
            return Object.values(this.filters).every(x => x === null || x === '' || x === undefined || x.included.length === 0)
        },

        demoFilterEmpty() {
            return this.$_.isArray(this.demoFilters) ? this.demoFilters.length === 0 : Object.keys(this.demoFilters).length === 0
        },

        noFilterResults() {
            let filterNum = this.filterEmpty ? 0 : Object.values(this.filters)
                .filter(x => x !== undefined)
                .map(filter => filter.included.length)
                .reduce((a, b) => a + b, 0)

            let totalFilters = filterNum + (this.demoFilterEmpty ? 0 : this.$_.isArray(this.demoFilters) ? this.demoFilters.length : Object.keys(this.demoFilters).length)
            
            // Fix race condition where first data point could be null while others were still loading
            return totalFilters !== 0 && !this.tabData[this.activeTab.show].data?.some(d => d.chronic_absence_rate !== null)
        },

        canManageSchools() {
            return this.$store.state.global.sessionUser?.security_codes?.includes("MANAGE_SCHOOLS")
        },

        noSchoolGroups() {
            return this.activeTab.show === 'school_group' && this.tabData[this.activeTab.show].loaded && !this.districtHasSchoolGroups
        },

        activeTabIndex() {
            return this.tabs.findIndex(tab => tab.show === this.activeTab.show)
        },

        crudConfig() {
            return this.$_.cloneDeep(this.$models.schoolGroups)
        },


        visibleColumnDefs() {
            return this.columnDefs.filter(col => !col.hide)
        },

        isFilteringByMealStatus() {
            return this.moreFiltersRawData?.meal_status_id?.length > 0
        },

        canSearchStudentsByMealStatus() {
            return this.viewMode.name === 'Data Wall' && this.isFilteringByMealStatus && !this.$hasSecurity('DRILLDOWN_MEAL_STATUS')
        },

        showByParticipation() {
            return this.$store.getters['flags/flags']['ec-interventions-by-participation'] === true
        }
    },

    watch: {
        schoolsLoading() {
            // Wait for FeFilterBtn to receive the new items prop before refreshing it
            this.$nextTick(this.syncSchoolFilter)
        },
        'filters.school_id': {
            handler(value, oldValue) {
                if (value !== oldValue) {
                    this.filterStateChange = true
                    if (this.viewMode.name === 'Data Wall') {
                        this.apply()
                    }
                }
            }
        },
        'filters.grade_id': {
            handler(value, oldValue) {
                if (value !== oldValue) {
                    this.filterStateChange = true
                    if (this.viewMode.name === 'Data Wall') {
                        this.apply()
                    }
                }
            }
        },
        'filters.ethnicity': {
            handler(value, oldValue) {
                if (value !== oldValue) {
                    this.filterStateChange = true
                    if (this.viewMode.name === 'Data Wall') {
                        this.apply()
                    }
                }
            }
        },
        'filters.gender': {
            handler(value, oldValue) {
                if (value !== oldValue) {
                    this.filterStateChange = true
                    if (this.viewMode.name === 'Data Wall') {
                        this.apply()
                    }
                }
            }
        },
        'viewMode.name': {
            handler(newValue) {
                if (newValue === 'Data Wall') {
                    this.lastActiveTab = this.activeTab.show
                    // Only block data loading if meal status filters are specifically applied AND user lacks permissions
                    if (this.isFilteringByMealStatus && !this.$hasSecurity('DRILLDOWN_MEAL_STATUS')) {
                    } else {
                        this.loadDataWallData()
                    }
                } else if (newValue === 'Charts' && this.lastActiveTab !== null) {
                    this.activeTab = this.tabs.find(tab => tab.show === this.lastActiveTab)
                    this.tabData[this.activeTab.show].loaded = false
                    this.loadChartData()
                }
            }
        },
        phaseTwoFlag: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            const chartsButton = document.getElementById('toggle-btn-0')
                            const dataWallButton = document.getElementById('toggle-btn-1')
                            
                            if (chartsButton) {
                                chartsButton.setAttribute('data-test', 'chronic-absence-charts-btn')
                            }
                            
                            if (dataWallButton) {
                                dataWallButton.setAttribute('data-test', 'chronic-absence-data-wall-btn')
                            }
                        }, 500)
                    })
                }
            }
        },

        // Watch for changes in meal status filters to update data wall display
        isFilteringByMealStatus: {
            handler(newVal, oldVal) {
                // Force reactivity update for data wall when meal status filters change
                if (this.viewMode.name === 'Data Wall') {
                    this.$forceUpdate()
                }
            }
        }
    },

    async mounted() {
        if (this.defaultSchool) {
            this.filters.school_id = {exluded: [], included: [this.defaultSchool]}
            this.updateChips = true
        }

        // Restore column visibility preferences from session storage
        this.restoreColumnPreferences()

        if (this.showByParticipation) {
            let interventionCol = this.columnDefs.find(x => x.field === "interventions")
            if (interventionCol) interventionCol.cellRendererFramework = 'interventionColumn'
        } else {
            this.columnDefs = this.columnDefs.filter(x => x.field !== "interventions")
        }

        this.$nextTick(() => {
            const tabElements = document.querySelectorAll('#ca-page-tabs .v-tab')
            const dataTestMap = {
                'district': 'chronic-absence-grouping-overall',
                'school': 'chronic-absence-grouping-school',
                'grade': 'chronic-absence-grouping-grade',
                'ethnicity': 'chronic-absence-grouping-demographic',
                'school_group': 'chronic-absence-grouping-school-group'
            }
            
            this.tabs.forEach((tab, index) => {
                if (tabElements[index] && dataTestMap[tab.show]) {
                tabElements[index].setAttribute('data-test', dataTestMap[tab.show])
                }
            })
        })
    },

    async beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.$nextTick(() => {
                // Restore last active tab from localStorage BEFORE loading data
                const savedTab = localStorage.getItem('lastActiveTab_chronicabsence')
                if (savedTab) {
                    const foundTab = vm.tabs.find(tab => tab.show === savedTab)
                    if (foundTab) {
                        vm.activeTab = foundTab
                    }
                }
                
                // Restore last view mode from localStorage
                const savedViewMode = localStorage.getItem('lastViewMode_chronicabsence')
                if (savedViewMode) {
                    vm.viewMode = { 
                        name: savedViewMode, 
                        icon: savedViewMode === 'Charts' ? 'fa-chart-bar' : 'fa-table' 
                    }
                }
                
                // Restore last selected time period from localStorage
                const savedTimePeriod = localStorage.getItem('lastTimePeriod_chronicabsence')
                if (savedTimePeriod) {
                    vm.selectedTimePeriod = JSON.parse(savedTimePeriod)
                }
                
                vm.checkSchoolGroups().then(hasGroups => {
                    vm.districtHasSchoolGroups = hasGroups
                })
                
                // Load combined school data (schools + school groups)
                vm.loadCombinedSchoolData()
                
                vm.tabData[vm.activeTab.show].loaded = false
                
                // Load data based on view mode
                if (vm.viewMode.name === 'Data Wall') {
                    vm.loadDataWallData()
                } else {
                    vm.loadChartData()
                }
                
                setTimeout(() => {
                    if (vm.$el?.parentElement) {
                        vm.$el.parentElement.scrollTop = 0
                    }
                }, 100)
            })
        })
    },

    methods: {
        prefsFetched(v) {
            let prefs = {}
            v.forEach(pref => {
                prefs[pref.code] = pref.user_value !== "0"
            })
            this.prefs.sisReportedConfigured = prefs['ATTENDANCE_EQUIV_RATE']
            this.ready = true
        },

        restoreColumnPreferences() {
            try {
                const savedPreferences = sessionStorage.getItem('chronicAbsence_columnPreferences')
                if (savedPreferences) {
                    const preferences = JSON.parse(savedPreferences)
                    // Apply saved preferences to column definitions
                    this.columnDefs.forEach(column => {
                        if (preferences.hasOwnProperty(column.field)) {
                            column.hide = preferences[column.field]
                        }
                    })
                }
            } catch (error) {
                console.warn('Failed to restore column preferences:', error)
            }
        },

        saveColumnPreferences() {
            try {
                const preferences = {}
                this.columnDefs.forEach(column => {
                    preferences[column.field] = column.hide || false
                })
                sessionStorage.setItem('chronicAbsence_columnPreferences', JSON.stringify(preferences))
            } catch (error) {
                console.warn('Failed to save column preferences:', error)
            }
        },

        goBack() {
            this.$router.push('/')
        },

        baseConfig() {
            let me = this
            let config = _.cloneDeep(baseConfig)
            config.plotOptions.column.dataLabels.enabled = this.countsToggle
            if (this.phaseTwoFlag) {
                config.tooltip.formatter = function() {
                    return me.formatTooltip(this)
                }
            }
            return config
        },

        getMonthFromString(mon){
            let date = Date.parse(mon + "1, 2012")
            if(!isNaN(date)){
                return new Date(date).getMonth() + 1
            }
            return -1
        },

        getLastDayOfMonth(year, month) {
            const today = new Date()
            let endDate
            if (month === today.getMonth() + 1) {
                endDate = today.getDate()
            } else {
                let newDate = new Date(year, month, 0)
                endDate = newDate.getDate()
            }
            return endDate
        },

        formatTooltip(chartData) {
            let tooltip
            let seriesStart = chartData.series.name.split('-')[0]
            let seriesEnd = chartData.series.name.split('-')[1]

            let seriesName

            if (this.activeTab.show === 'district') {
                if (this.rateDescriptor === 'Month') {
                    let startMonth = this.getMonthFromString(chartData.series.data[0].category)
                    let endMonth = this.getMonthFromString(chartData.series.data[chartData.series.data.length - 1].category)
                    let endDate = this.getLastDayOfMonth(seriesEnd, endMonth)
                    tooltip = `<b>${startMonth}/1/${seriesStart} - ${endMonth}/${endDate}/${seriesEnd}</b><br/>`

                    seriesName = chartData.series.name
                } else {
                    seriesStart = chartData.x.split('-')[0]
                    seriesEnd = chartData.x.split('-')[1]
                    tooltip = `<b>${seriesStart} - ${seriesEnd}</b><br/>`

                    seriesName = chartData.key
                }
            } else {
                tooltip = `<b>${seriesStart} - ${seriesEnd} ${chartData.point.category}</b><br/>`
                seriesName = chartData.series.name
            }

            let currentData = this.tabData[this.activeTab.show].data.find(d => {
                let tabSeriesName = `${d.start_date}-${d.end_date}`
                return tabSeriesName === seriesName && d.chronic_absence_rate === chartData.point.y
            })

            const startDate = this.$dayjs(currentData.start_display_date).isValid() ? this.$dayjs(currentData.start_display_date).format('M/D/YYYY') : currentData.start_display_date
            const endDate = this.$dayjs(currentData.end_display_date).isValid() ? this.$dayjs(currentData.end_display_date).format('M/D/YYYY') : currentData.end_display_date
            tooltip = `<b>${startDate} - ${endDate}</b><br/>`
            tooltip += `<span style="color:${chartData.point.color}">\u25CF</span> Chronic absence rate: <b>${chartData.point.y}</b>%<br/>`
            tooltip += `<span style="color: rgba(0,0,0,0)">\u25CF</span> Student count: <b>${new Intl.NumberFormat().format(currentData.chronically_absent_students)} of ${new Intl.NumberFormat().format(currentData.enrolled_students)} students</b><br/>`
            return tooltip
        },

        async loadChartData() {
            // Safety check: Ensure non-district tabs always use annual view
            if (this.activeTab.show !== 'district' && this.selectedTimePeriod.id.includes('M')) {
                const yearCount = this.selectedTimePeriod.id[0]
                const yearName = yearCount === '1' ? 'This Year' : `Last ${yearCount} Years`
                this.selectedTimePeriod = {name: `${yearName} (Annually)`, id: `${yearCount}Y`}
            }
            
            let url = this.$models.getUrl('chronicAbsenceTrends', 'read')
            let params = this.requestParams()

            this.tabData[this.activeTab.show] = {
                ...this.tabData[this.activeTab.show],
                loaded: false,
                error: false
            }

            try {
                const response = await this.$axios.get(url, {params: params})
                await this.$nextTick()
                this.tabData[this.activeTab.show] = {
                    data: response.data.trend,
                    info: response.data.info,
                    lastLoadedTimePeriod: this.selectedTimePeriod.id,
                    error: false,
                    loaded: true
                }
            } catch (error) {
                console.error('Error loading chart data:', error)
                this.tabData[this.activeTab.show] = {
                    ...this.tabData[this.activeTab.show],
                    loaded: true,
                    error: true
                }
            }
        },

        requestParams() {
            let obj = {
                selected_time_period: this.selectedTimePeriod.id,
                filters: {},
                demoFilters: {}
            }
            if (this.activeTab.show !== 'district') obj['group_by'] = this.activeTab.show
            if (this.activeTab.show === 'ethnicity') obj['group_by'] = this.selectedDemographic

            for (const filter in this.filters) {
                let thisFilter = this.filters[filter]

                if (thisFilter?.included?.length) {
                    let searchArray = []
                    thisFilter.included.forEach(filterItem => {
                        if (filterItem.hasOwnProperty('id')) {
                            // Pass each school id associated with any selected school group filter
                            if (filter === 'school_id' && filterItem.is_school_group) {
                                searchArray.push(...filterItem.school_ids)
                            } else {
                                searchArray.push(filterItem.id)
                            }
                        } else if (filterItem?.display_name_group?.length) {
                            searchArray.push(...filterItem.display_name_group)
                        } else if (filterItem.hasOwnProperty(filter)) {
                            searchArray.push(filterItem[filter])
                        }
                    })
                    obj.filters[filter] = searchArray
                }
            }

            // Handle default demographic filters from MoreFilters (stored in moreFiltersRawData)
            const defaultDemographicFilters = this.convertForApi()
            Object.assign(obj.demoFilters, defaultDemographicFilters)
            
            // Handle custom demographic filters from MoreFilters (use raw data for API)
            for (const demoFilter in this.moreFiltersRawData) {
                // Skip default demographic filters since they're handled above
                if (!['disability_type_id', 'ell_level_id', 'meal_status_id', 'ell_flag'].includes(demoFilter)) {
                    const rawValue = this.moreFiltersRawData[demoFilter]
                    
                    // Handle different custom demographic types correctly  
                    if (typeof rawValue === 'string' && !rawValue.includes('eq:') && !rawValue.includes(':')) {
                        // Option demographic (multi-select without operator) - convert to array
                        if (rawValue.includes(',')) {
                            // Multi-select: "2,3" → [2, 3]
                            obj.demoFilters[demoFilter] = rawValue.split(',').map(id => parseInt(id.trim()))
                } else {
                            // Single select: "2" → [2]
                            obj.demoFilters[demoFilter] = [parseInt(rawValue.trim())]
                }
                    } else {
                        // Send boolean filters and operator filters as is
                        obj.demoFilters[demoFilter] = rawValue
            }
                }
            }

            return obj
        },

        removeChipFilter(filter, chipIndex) {
                if (filter.name !== undefined) {
                    this.filters[filter.name] = undefined
                }

                if (this.$_.isArray(this.demoFilters)) {
                    this.demoFilters = this.demoFilters.filter(f => f !== filter)
                } else {
                    delete this.demoFilters[filter.key]
                }
                
                // Always delete from moreFiltersRawData when removing a filter
                if (filter.key) {
                    this.$delete(this.moreFiltersRawData, filter.key)
                }
                
                this.filterStateChange = true
                this.apply()
        },

        clearFilter(filterRef) {
            let chipToClear = this.filterChips.find(chip => chip.name.includes(filterRef.key ? filterRef.key : filterRef))
            if(filterRef.key && this.$refs[`${filterRef.key}-filter-menu`]) {
                this.$refs[`${filterRef.key}-filter-menu`].clearSelections()
            }
            if (chipToClear !== undefined) {
                this.removeChipFilter(chipToClear)
            }
        },

        clearAllFilters() {
            this.filterStateChange = true
            this.filters = {school_id: undefined, grade_id: undefined, ethnicity: undefined, gender: undefined}
            this.demoFilters = {}
            this.moreFiltersRawData = {}
            this.updateChips = true
            this.apply()
        },

        apply(filter) {
            if (this.filterStateChange) {
                this.filterStateChange = false
                if (this.viewMode.name === 'Charts') {
                    this.tabData = {
                        district: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                        school: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                        grade: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                        ethnicity: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                        school_group: {data: undefined, info: undefined, loaded: false, lastLoadedTimePeriod: undefined, error: false},
                    }
                    this.loadChartData()
                } else if (this.viewMode.name === 'Data Wall') {
                    this.loadDataWallData()
                }
                
            }
            if (filter) {
                this.$refs[filter].openMenu = false
            }
            this.updateChips = true
        },

        applyMore(filterData) {
            this.moreFiltersRawData = filterData
            
            // Transform to display format for FilterChips
            
            const displayFilters = {}
            
            for (const [key, value] of Object.entries(filterData)) {
                if (value !== null && value !== undefined) {
                    // Check if this needs Yes/No conversion with contextual prefix
                    const needsPrefix = 
                        (typeof value === 'string' && (value.includes('eq:1') || value.includes('eq:0'))) ||
                        (key === 'ell_flag' && (value === '1' || value === '0'))
                    
                    if (needsPrefix) {
                        const displayValue = key === 'ell_flag' 
                            ? (value === '1' ? 'Yes' : 'No')
                            : (value.includes('eq:1') ? 'Yes' : 'No')
                        
                        // For custom demographics, get the real name from the store
                        let demoLabel
                        if (key.startsWith('demo_')) {
                            const demoId = key.split('_')[1]
                            const demographic = this.demographics.find(demo => demo.id === parseInt(demoId))
                            demoLabel = demographic.name
                        } else {
                            demoLabel = 'EL Status'
                        }
                        
                        displayFilters[key] = [{
                            name: displayValue,
                            id: value,
                            label: demoLabel
                        }]
                    } else if (Array.isArray(value) && value.length > 0) {
                        // Default demographic arrays (disability, ELP level, meal status)
                        const labelMap = {
                            'disability_type_id': 'Disability',
                            'ell_level_id': 'ELP Level', 
                            'meal_status_id': 'Meal Status'
                        }
                        displayFilters[key] = value.map(item => ({
                            name: item.description || item.display_name || item.name,
                            id: item.id,
                            label: labelMap[key] || ''
                        }))
                    } else if (!['disability_type_id', 'ell_level_id', 'meal_status_id', 'ell_flag'].includes(key) && 
                               !(typeof value === 'string' && (value.includes('eq:1') || value.includes('eq:0')))) {
                        // Custom demographic options - use DemographicUtil for consistent formatting of strings
                        if (typeof value === 'string') {
                            const chipDisplay = DemographicUtil.getDemoName(this.demographics, [key, value], false)  // descriptiveChip = false (optimize for chips)
                            const fullDemoName = DemographicUtil.getDemoName(this.demographics, [key, value])  // descriptiveChip = true (default, for name extraction)
                            
                            if (chipDisplay && fullDemoName) {
                                const demoName = fullDemoName.split(':')[0]  // Extract just the name part
                                displayFilters[key] = [{
                                    name: chipDisplay,
                                    id: value,
                                    label: demoName
                                }]
                            } else {
                                // Fallback: use a generic label if DemographicUtil can't process it
                                displayFilters[key] = [{
                                    name: value,  // Raw value as fallback
                                    id: value,
                                    label: key.replace('demo_', 'Custom Demo ')  // Generic fallback label
                                }]
                            }
                        } else {
                            // Pass through non-string values unchanged
                    displayFilters[key] = value
                }
            }
                }
            }
            this.demoFilters = displayFilters
            
            // Update chips
            this.updateChips = false
            this.$nextTick(() => {
                this.updateChips = true
            })
            
            // Trigger data reload
            if (this.viewMode.name === 'Data Wall') {
                this.loadDataWallData()
            } else {
                this.loadChartData()
            }
        },
    
        tabClicked(tab) {
            const previousTab = this.activeTab.show
            
            this.tabData[this.activeTab.show].loaded = false
            this.tabData[tab.show].loaded = false
            this.activeTab = tab
            
            // Save active tab to localStorage
            localStorage.setItem('lastActiveTab_chronicabsence', tab.show)
            
            // When switching FROM district TO any other tab, convert monthly to annual
            if (previousTab === 'district' && tab.show !== 'district' && this.selectedTimePeriod.id.includes('M')) {
                const yearCount = this.selectedTimePeriod.id[0]
                const yearName = yearCount === '1' ? 'This Year' : `Last ${yearCount} Years`
                this.selectedTimePeriod = {name: `${yearName} (Annually)`, id: `${yearCount}Y`}
            }
            // When switching FROM any other tab TO district, keep the current selection (no conversion)
            
            // Check for school groups when switching to school group tab
            if (tab.show === 'school_group') {
                this.checkSchoolGroups().then(hasGroups => {
                    this.districtHasSchoolGroups = hasGroups
                    if (hasGroups) {
                        this.loadChartData()
                    }
                })
                return
            }
            
            this.loadChartData()
        },

        chartParamChange(type, value, toggleUsed) {
            switch(type) {
                case 'timePeriod':
                    this.selectedTimePeriod = value
                    // Save the selected time period to localStorage
                    localStorage.setItem('lastTimePeriod_chronicabsence', JSON.stringify(value))
                    if (this.tabData[this.activeTab.show].loaded) this.tabData[this.activeTab.show].loaded = false
                    if (this.viewMode.name === 'Charts') {
                        this.loadChartData()
                    } else if (this.viewMode.name === 'Data Wall') {
                        this.loadDataWallData()
                    }
                    break
                case 'countToggle':
                    this.useDefaultToggle = !toggleUsed
                    this.countsToggle = value
                    break
                case 'demographic':
                    this.selectedDemographic = value.id
                    this.tabData.ethnicity = {data: undefined, loaded: false, error: false}
                    if (this.viewMode.name === 'Charts') {
                        this.loadChartData()
                    } else if (this.viewMode.name === 'Data Wall') {
                        this.loadDataWallData()
                    }
                    break
                default:
            }
        },

        setExportingParams(config, group) {
            let me = this
            let fileName = group === 'district' ? 'Chronic Absence Rate Overall' : `Chronic Absence Rate By ${group}`
            config.exporting.filename = fileName
            config.exporting.csv = {
                columnHeaderFormatter: function(item, key) {
                    if (item.isXAxis) {
                        return me.rateDescriptor ? me.rateDescriptor : group
                    } else {
                        return item.name.includes('Series') ? 'Rate (%)' : item.name
                    }
                }
            }
        },

        setAxisLabels(grouped, config, info) {
            if (this.tabData[this.activeTab.show].data !== undefined) {
                if (Object.keys(grouped).length > 1) {
                    config.legend.enabled = true
                } else {
                    config.xAxis.title.text = `${info.start_date} - ${info.end_date}`
                    config.xAxis.title.offset = 70
                }
            }
        },

        setSeriesPattern(series, config) {
            // Bars should be the same color when there is no legend
            const patterns = series.length <= 2 ? twoSeriesPatterns : fiveSeriesPatterns
            
            series.forEach((seriesItem, index) => {
                if (config.legend.enabled) {
                    const pattern = patterns[index % patterns.length]
                    seriesItem.color = pattern
                    if (pattern.patternIndex) seriesItem.color.borderColor = '#8085e9'
                } else {
                    seriesItem.color = singleSeriesPattern[0]
                }
            })
        },

        setDataLabelToggle() {
            let toggleOffOptions = (this.selectedTimePeriod.id === '5M') || (this.selectedTimePeriod.id === '5Y' && this.activeTab.show !== 'district')
            if (this.useDefaultToggle && toggleOffOptions) this.chartParamChange('countToggle', false, false)
            else if (this.useDefaultToggle) this.chartParamChange('countToggle', true, false)
        },

        goToStudentProfile(studentId, schoolYearId) {
            this.showChronicAbsenceDetails = false
            
            // Use provided school year (should always exist since student is in the list for this year)
            const yearId = schoolYearId
            
            this.$router.push({ 
                name: 'StudentProfile',
                params: { 
                    id: studentId,
                    year: yearId
                }
            }).catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                    console.error('Error navigating to student profile:', err)
                }
            })
        },

        handleChartDrilldown(data) {
            const categoryName = data.category || 'Overall'
            const seriesName = data.series
            const value = data.value
            
            const isFilteringByMealStatus = this.selectedDemographic === 'meal_status' || 
                                           this.activeTab.show === 'meal_status' || 
                                           this.isFilteringByMealStatus
            
            const shouldShowModal = isFilteringByMealStatus && !this.$hasSecurity('DRILLDOWN_MEAL_STATUS')
            
            if (shouldShowModal) {
                this.showMealStatusPermissionModal = true
                return
            }
            
            // Get the data element for this specific bar to extract school_year_id and group_by_value
            const currentData = this.tabData[this.activeTab.show].data?.find(d => {
                const rateMatch = d.chronic_absence_rate === value
                
                // For district annual view, use categoryName instead of seriesName
                const timeMatch = this.activeTab.show === 'district' && this.rateDescriptor === 'Year'
                    ? `${d.start_date}-${d.end_date}` === categoryName
                    : `${d.start_date}-${d.end_date}` === seriesName
                
                // Match on category (grade/school/etc.) to ensure correct bar when duplicate rates exist
                const categoryMatch = this.activeTab.show === 'district'
                    ? true
                    : this.activeTab.show === 'ethnicity'
                    ? d[this.selectedDemographic] === categoryName
                    : d[this.activeTab.show] === categoryName
                
                return rateMatch && timeMatch && categoryMatch
            })
            
            if (!currentData) {
                console.error('Could not find data for clicked bar')
                return
            }

            const baseParams = this.requestParams()
            const params = {
                ...baseParams,
                school_year_id: currentData.school_year_id,
                group_by_value: currentData.group_by_value
            }
            
            // Set dialog title and show dialog
            const startDate = currentData?.start_display_date && this.$dayjs(currentData.start_display_date).isValid() ? this.$dayjs(currentData.start_display_date).format('M/D/YYYY') : currentData?.start_display_date || ''
            const endDate = currentData?.end_display_date && this.$dayjs(currentData.end_display_date).isValid() ? this.$dayjs(currentData.end_display_date).format('M/D/YYYY') : currentData?.end_display_date || ''
            this.chronicAbsenceDetailsTitle = `Chronically Absent Students ${startDate} - ${endDate}`
            const csvStartDate = currentData?.start_display_date && this.$dayjs(currentData.start_display_date).isValid() ? this.$dayjs(currentData.start_display_date).format('M/D/YYYY') : currentData?.start_display_date || ''
            const csvEndDate = currentData?.end_display_date && this.$dayjs(currentData.end_display_date).isValid() ? this.$dayjs(currentData.end_display_date).format('M/D/YYYY') : currentData?.end_display_date || ''
            this.datawallModalCSVFilename = `Chronically Absent Students ${csvStartDate}_${csvEndDate}`
            this.showChronicAbsenceDetails = true
            this.chronicAbsenceDetailsParams = params
            
            // Load the chronic absence details data
            this.loadChronicAbsenceDetails(params)
        },

        async loadChronicAbsenceDetails(params) {
            this.chronicAbsenceDetailsLoading = true
            
            try {
                const url = this.$models.getUrl('chronicAbsenceDatawall', 'read')
                const response = await this.$axios.get(url, { params })
                this.gridData = response.data.datawall?.students || []
            } catch (error) {
                console.error('Error loading chronic absence details:', error)
                this.gridData = []
            } finally {
                this.chronicAbsenceDetailsLoading = false
            }
        },


        reloadModalData() {
            if (this.chronicAbsenceDetailsParams) {
                this.loadChronicAbsenceDetails(this.chronicAbsenceDetailsParams)
            }
        },


        async loadDataWallData() {
            // Data wall uses current school year instead of selected time period
            const params = this.requestParams()
            params.school_year_id = this.currentYear.id
            delete params.selected_time_period
            
            try {
                const url = this.$models.getUrl('chronicAbsenceDatawall', 'read')
                const response = await this.$axios.get(url, {params: params})
                this.gridData = response.data.datawall?.students || []
            } catch (error) {
                console.error('Error loading data wall data:', error)
                this.gridData = []
            }
        },

        async checkSchoolGroups() {
            try {
                const response = await this.$axios.get(this.$models.getUrl('schoolGroups', 'read'))
                return response.data.school_group_v && response.data.school_group_v.length > 0
            } catch (error) {
                console.error('Error checking school groups:', error)
                return false
            }
        },

        async loadCombinedSchoolData() {
            this.schoolsLoading = true
            try {
                const [schools, schoolGroups] = await Promise.all([
                    this.$axios.get(this.schoolUrl),
                    this.$axios.get(this.schoolGroupUrl)
                ])

                const schoolsData = schools.data.schools || []
                const schoolGroupsData = schoolGroups.data.school_group_v || []

                // Only show school groups with at least one school the user can access
                const accessibleSchoolIds = new Set(schoolsData.map(school => school.id))
                const filteredGroups = schoolGroupsData.filter(group => {
                    return group.schools && group.schools.some(schoolId => accessibleSchoolIds.has(schoolId))
                })

                const transformedGroups = filteredGroups.map(group => ({
                    id: `group_${group.id}`,
                    name: group.name,
                    is_school_group: true,
                    school_ids: group.schools,
                    original_group_id: group.id
                }))

                this.combinedSchoolsData = { schools: [...schoolsData, ...transformedGroups] }
                
                // Set userHasSchoolGroups based on whether we have any accessible groups
                this.userHasSchoolGroups = transformedGroups.length > 0
            } catch (error) {
                console.error('Error loading combined school data:', error)
                this.combinedSchoolsData = { schools: [] }
                this.userHasSchoolGroups = false
            } finally {
                this.schoolsLoading = false
            }
        },

        // FeFilterBtn has no loading prop, and its items watcher does not rebuild
        // displayItems, so an already-open menu stays empty. Push both in directly.
        syncSchoolFilter() {
            const filter = this.$refs['ca-school-filter']
            if (!filter) return
            filter.loading = this.schoolsLoading
            filter.getItems()
        },

        convertForApi() {
            const apiFilters = {}
            
            // Handle multi-select default demographic filters (arrays)
            const multiSelectFilters = {
                'disability_type_id': 'disability_type_id',
                'ell_level_id': 'ell_level_id', 
                'meal_status_id': 'meal_status_id'
            }
            
            Object.entries(multiSelectFilters).forEach(([filterKey, apiKey]) => {
                if (this.moreFiltersRawData[filterKey]?.length) {
                    apiFilters[apiKey] = this.moreFiltersRawData[filterKey].map(item => item.id)
                }
            })
            
            // Handle EL Status (single value) - comes as string ID
            if (this.moreFiltersRawData.ell_flag != null) {
                apiFilters.ell_flag = parseInt(this.moreFiltersRawData.ell_flag)
            }
            
            return apiFilters
        },
        
        onApplyFilters(field, filterType, filters, applyNew=true) {
            let newFilter = []
            if (filters.interventionLevels === null) {
                this.scoreFilters = []

                // if saved filter is being removed, temporarily remove it
                // it will return if/when the dw reloads
                let index = this.savedFilters.indexOf(this.savedFilters.find(f => f.name === field))
                if (index >= 0) this.savedFilters.splice(index, 1)
            } else {
                newFilter = [{
                    type: filterType,
                    field: field,
                    filters: filters,
                }]
                // add new filter to existing filter(s) instead of removing past filter(s)
                this.scoreFilters = this.scoreFilters.concat(newFilter)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.permission-dialog {
    :deep(*) {
        border-radius: 20px !important;
    }
    
    :deep(.fe-dialog),
    :deep(.fe-dialog-content),
    :deep(.modal-dialog),
    :deep(.modal-content),
    :deep(.dialog),
    :deep(.dialog-content) {
        border-radius: 20px !important;
    }
}

.permission-modal-content {
    padding: 20px 0;
    
    .permission-message {
        margin-bottom: 24px;
        line-height: 1.5;
        color: #333;
    }
    
    .permission-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        
        .learn-more-link {
            color: #006C90;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 6px;
            font-weight: 500;
            font-size: 16px;
            
            &:hover {
                text-decoration: underline;
            }
            
            i {
                color: #006C90;
                font-size: 16px;
            }
        }
    }
}

.permission-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 400px;
    background: #fff;
    border: 1px solid #E0E1E6;
    border-radius: 5px;
    margin: 32px 52px 0 52px;
    
    .permission-empty-content {
        text-align: center;
        max-width: 500px;
        padding: 40px 20px;
        
        .permission-icon {
            font-size: 32px;
            margin-bottom: 24px;
            color: #495B8F;
            
            i {
                color: #495B8F;
            }
        }
        
        .permission-title {
            font-size: 14px;
            font-weight: 600;
            color: #050F2D;
            margin-bottom: 8px;
        }
        
        .permission-message {
            font-size: 14px;
            color: #333;
            line-height: 1.5;
            margin-bottom: 24px;
        }
        
        .permission-actions {
            display: flex;
            justify-content: center;
            
            ::v-deep .fe-button,
            ::v-deep .v-btn,
            ::v-deep .v-btn__content {
                text-transform: none !important;
            }
        }
    }
}

.chart-area {
    padding: 32px 52px 0 52px;
}

.chart-area.demographic-tab {
    padding: 22px 52px 0 52px;
}

.chronic-absence-container {
    background-color: #F3F5F7;
    min-height: 100vh;
}

.header-section {
    background-color: white;
    padding: 20px 38px 20px 40px;
    border-bottom: 1px solid #e0e0e0;
}

.ca-tabs {
    height: 28px !important;
    margin-left: 12px;

    ::v-deep .fe-tabs-default {
        padding-left: 0 !important
    }
    ::v-deep .fe-tabs a {
        padding-bottom: 15px;
    }
    ::v-deep .fe-tabs .v-tabs-slider {
        width: 100% !important;
        left: 0 !important;
    }
    ::v-deep .fe-tabs-default .v-tab {
        font-size: 14px;
    }
    ::v-deep .fe-tabs-default-color a {
        color: #495B8F !important;
        font-weight: 500 !important;

        &:hover {
            span {
                opacity: 1 !important;
            }
        }
    }
    ::v-deep .fe-tabs-default .v-tab--active a {
        color: #006C90 !important;
    }
    ::v-deep .fe-tabs-default .v-tab:hover {
        background-color: #ECEDF1;
    }
    ::v-deep .fe-tabs-default .v-tab--active:hover {
        background-color: #E5F0F4;
    }
}

.center-demo-combo-title {
    font-weight: bold;
    font-size: 16px;
    padding-top: 8px;
    padding-right: 8px;
}

.demographic-select {
    max-width: 20%;
}

.data-area {
    width: 100%;
    overflow: hidden;
    margin: 0;
    padding: 0;
}

.chronic-absence-grid {
    width: 100%;
    /* Height is set dynamically via inline style */
}

.ag-bold-header {
    font-weight: bold !important;
}

a.back-button {
    cursor: pointer;
    text-decoration: none;
    transition: color 0.2s ease;
    color: #006c90;
    font-size: 16px;
    width: 12vw;
    
    i {
        color: #006c90;
        margin-right: 4px;
    }
    
    &:hover {
        color: #005673 !important;
        text-decoration: underline;
        
        i {
            color: #005673 !important;
        }
    }
    
    &:focus-visible {
        outline: 2px solid #2B87FF !important;
        outline-offset: 2px;
        border-radius: 2px;
    }
    
    &:focus:not(:focus-visible) {
        outline: none;
    }
}

::v-deep .v-menu {
    z-index: 50 !important;
}
.toggle-btn {
    margin-right: 14px;
}
</style>

